import React, { useState } from 'react';
import { CourseContent } from '@/types/course';
import {
  ChevronRightIcon,
  ChevronDownIcon,
  DocumentIcon,
  PlayCircleIcon,
  FolderIcon,
  QuestionMarkCircleIcon,
  EyeIcon
} from '@heroicons/react/24/outline';

interface CourseContentTreeProps {
  content: CourseContent[];
  className?: string;
}

interface ContentModalProps {
  content: CourseContent;
  isOpen: boolean;
  onClose: () => void;
}

const ContentModal: React.FC<ContentModalProps> = ({ content, isOpen, onClose }) => {
  if (!isOpen) return null;

  const sanitizeContent = (htmlContent: string) => {
    // Remove anchor tags containing video links
    const anchorVideoPattern = /<a[^>]*href=["']https:\/\/m2mba\.com\/api\/v1\/videos\/watch\?v=[^"']*["'][^>]*>.*?<\/a>/gi;
    let sanitized = htmlContent.replace(anchorVideoPattern, '<span class="text-m2-on-surface-variant italic">[Video link removed for preview]</span>');
    
    // Remove standalone video links
    const videoLinkPattern = /https:\/\/m2mba\.com\/api\/v1\/videos\/watch\?v=[^\s<>"']+/g;
    sanitized = sanitized.replace(videoLinkPattern, '<span class="text-m2-on-surface-variant italic">[Video link removed for preview]</span>');
    
    return sanitized;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-m2-surface-container rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden">
        <div className="flex justify-between items-center p-6 border-b">
          <h3 className="text-xl font-semibold text-m2-on-surface">{content.name}</h3>
          <button
            onClick={onClose}
            className="text-m2-outline hover:text-m2-on-surface-variant transition-colors"
          >
            <span className="sr-only">Đóng</span>
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          <div className="prose prose-lg max-w-none
            [&_img]:max-w-full [&_img]:h-auto [&_img]:rounded-lg [&_img]:shadow-md [&_img]:my-4
            [&_table]:border-collapse [&_table]:border [&_table]:border-m2-outline [&_table]:w-full [&_table]:my-4
            [&_table_th]:border [&_table_th]:border-m2-outline [&_table_th]:bg-m2-surface-low [&_table_th]:px-4 [&_table_th]:py-2 [&_table_th]:text-left [&_table_th]:font-semibold
            [&_table_td]:border [&_table_td]:border-m2-outline [&_table_td]:px-4 [&_table_td]:py-2
            [&_table_tbody_tr:nth-child(even)]:bg-m2-surface-low
            [&_ul]:list-disc [&_ul]:list-outside [&_ul]:pl-6 [&_ul]:space-y-2 [&_ol]:list-decimal [&_ol]:list-outside [&_ol]:pl-6 [&_ol]:space-y-2 [&_li]:pl-1 [&_p]:mb-2
            [&_blockquote]:border-l-4 [&_blockquote]:border-m2-primary [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:my-4
            [&_strong]:font-semibold [&_em]:italic">
            {content.detail ? (
              <div dangerouslySetInnerHTML={{ __html: sanitizeContent(content.detail) }} />
            ) : (
              <p className="text-m2-on-surface-variant">Nội dung không có sẵn.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const CourseContentTree: React.FC<CourseContentTreeProps> = ({ content, className = '' }) => {
  const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set());
  const [selectedContent, setSelectedContent] = useState<CourseContent | null>(null);
  
  // Debug logging removed

  // Build tree structure from flat array
  const buildTree = (items: CourseContent[]): CourseContent[] => {
    const itemMap = new Map<number, CourseContent & { children: CourseContent[] }>();
    const rootItems: (CourseContent & { children: CourseContent[] })[] = [];

    // Create map of all items with children array
    items.forEach(item => {
      itemMap.set(item.id, { ...item, children: [] });
    });

    // Build parent-child relationships
    items.forEach(item => {
      const itemWithChildren = itemMap.get(item.id)!;
      
      if (item.parentId && itemMap.has(item.parentId)) {
        itemMap.get(item.parentId)!.children.push(itemWithChildren);
      } else {
        rootItems.push(itemWithChildren);
      }
    });

    return rootItems;
  };

  const toggleExpanded = (itemId: number) => {
    const newExpanded = new Set(expandedItems);
    if (expandedItems.has(itemId)) {
      newExpanded.delete(itemId);
    } else {
      newExpanded.add(itemId);
    }
    setExpandedItems(newExpanded);
  };

  const handleItemClick = (item: CourseContent) => {
    if (item.isPublic && item.detail) {
      setSelectedContent(item);
    }
  };

  const getContentIcon = (contentType: string) => {
    switch (contentType.toUpperCase()) {
      case 'VIDEO':
        return PlayCircleIcon;
      case 'POST':
        return DocumentIcon;
      case 'FOLDER':
        return FolderIcon;
      case 'TEST':
        return QuestionMarkCircleIcon;
      default:
        return DocumentIcon;
    }
  };

  const renderTreeItem = (item: CourseContent & { children?: CourseContent[] }, level: number = 0) => {
  const hasChildren = Array.isArray(item.children) && item.children.length > 0;
    const isExpanded = expandedItems.has(item.id);
    const Icon = getContentIcon(item.contentType);
    
    // Convert numeric database values to proper booleans
    const isPublic = Boolean(item.isPublic);
    const hasDetail = Boolean(item.detail);
    const isClickable = isPublic && hasDetail;
    

    return (
      <div key={item.id} className="select-none">
        <div
          className={`flex items-center py-2 px-2 rounded transition-colors ${
            isClickable 
              ? 'hover:bg-m2-primary/10 cursor-pointer' 
              : 'text-m2-on-surface-variant'
          }`}
          style={{ marginLeft: `${level * 20}px` }}
          onClick={() => isClickable && handleItemClick(item)}
        >
          {/* Expand/Collapse button */}
          <div className="w-5 h-5 flex items-center justify-center mr-1">
            {hasChildren && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  toggleExpanded(item.id);
                }}
                className="p-0.5 hover:bg-m2-surface-highest rounded"
              >
                {isExpanded ? (
                  <ChevronDownIcon className="h-3 w-3 text-m2-on-surface-variant" />
                ) : (
                  <ChevronRightIcon className="h-3 w-3 text-m2-on-surface-variant" />
                )}
              </button>
            )}
          </div>

          {/* Content Icon */}
          <Icon className={`h-4 w-4 mr-2 ${
            item.contentType === 'VIDEO' ? 'text-purple-500' :
            item.contentType === 'POST' ? 'text-m2-primary' :
            item.contentType === 'FOLDER' ? 'text-yellow-500' :
            'text-m2-on-surface-variant'
          }`} />

          {/* Content Name */}
          <span className={`text-sm ${isClickable ? 'text-m2-on-surface font-medium' : 'text-m2-on-surface-variant'}`}>
            {item.name}
          </span>


          {/* Public indicator */}
          {isPublic && hasDetail && (
            <EyeIcon className="h-3 w-3 ml-2 text-green-500" />
          )}
        </div>

        {/* Children */}
        {hasChildren && isExpanded && (
          <div>
            {item.children!.map(child => renderTreeItem(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  if (!content || content.length === 0) {
    return (
      <div className={`text-center py-8 ${className}`}>
        <DocumentIcon className="h-12 w-12 text-m2-outline-variant mx-auto mb-3" />
        <p className="text-m2-on-surface-variant">Chưa có nội dung khóa học.</p>
      </div>
    );
  }

  const treeData = buildTree(content);

  return (
    <div className={`bg-m2-surface-container border rounded-lg p-4 ${className}`}>
      <div className="space-y-1">
        {treeData.map((item) => renderTreeItem(item, 0))}
      </div>

      {/* Content Detail Modal */}
      <ContentModal
        content={selectedContent!}
        isOpen={!!selectedContent}
        onClose={() => setSelectedContent(null)}
      />
    </div>
  );
};

export default CourseContentTree;